import React, { useState, useEffect } from "react";
import { Routes, Route, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./App.css";
import { FaCommentDots, FaPhone, FaVideo, FaExclamationTriangle } from "react-icons/fa";

function PlansPage() {
  const { jwtToken } = useParams();

  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiToken, setApiToken] = useState(null);


  useEffect(() => {
    let decodedUser;
    if (jwtToken) {
      try {
        decodedUser = jwtDecode(jwtToken);
        console.log("Decoded User:", decodedUser);
        setUser(decodedUser);
      } catch (err) {
        console.error("Invalid JWT token:", err);
        setError("Invalid or expired token provided in the URL.");
        setLoading(false);
        return;
      }
    } else {
      setError("No authentication token found in URL.");
      setLoading(false);
      return;
    }

    const loadPlans = async () => {
      try {
        // The JWT from the URL will be used directly for API calls.
        const token = jwtToken;
        setApiToken(token);
        console.log("Using JWT from URL for API calls.");

        // Fetch subscription plans using the token from the URL.
        const response = await fetch(
          "https://stage-api-zenit.trinodepointers.com/api/subscription/plans",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const apiResponse = await response.json();

        // Map API data to the structure needed by the component
        const mappedPlans = apiResponse.data.map((plan) => {
          let color, button;
          switch (plan.name) {
            case "Basic Plan":
              color = "primary";
              button = "Confirm & Proceed to Payment";
              break;
            case "Premium Plan":
              color = "primary";
              button = "Confirm & Proceed to Payment";
              break;
            default:
              color = "primary";
              button = "Confirm & Proceed to Payment";
          }

          const features = [];
          // Description is now handled separately
          if (plan.chat_duration) features.push({ text: `Chat Duration:`, value: `${plan.chat_duration} mins`, icon: FaCommentDots });
          if (plan.audio_call) features.push({ text: `Audio Calls:`, value: `${plan.call_duration} mins`, icon: FaPhone });
          if (plan.video_call) features.push({ text: `Video Calls:`, value: `${plan.call_duration} mins`, icon: FaVideo });

          return {
            id: plan.id,
            name: plan.name,
            price: `${plan.amount}`, // For display
            amount: plan.amount, // For payment processing
            features: features,
            description: plan.description, // Store description separately
            popular: plan.featured,
            color: color,
            button: button,
          };
        });

        setPlans(mappedPlans);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, [jwtToken]);

  const handlePayment = async (plan) => {
    // 1. Dynamically load the Razorpay script
    const loadScript = (src) => {
      return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };

    if (!user) {
      alert("User details not found. Cannot proceed with payment.");

      return;
    }

    // Fetch user info for prefill
    // Start with data from the JWT as a fallback.
    let prefillData = {
      name: user.name || "",
      email: user.email || "",
      contact: user.contact || "",
    };


    if (apiToken) {
      try {
        const userInfoResponse = await fetch("https://stage-api-zenit.trinodepointers.com/user/info", {
          headers: {
            Authorization: `Bearer ${apiToken}`,
          },
        });

        if (userInfoResponse.ok) {
          const userInfo = await userInfoResponse.json();
          // Safely log the entire user info object for debugging.
          console.log("User Info from API:", userInfo);

          // Overwrite with fresh data from the API if it exists.
          if (userInfo.data) {
            // If the API provides a name, use it. Otherwise, keep the existing one.
            if (userInfo.data.name) {
              prefillData.name = userInfo.data.name;
            }
            // If the API provides an email, use it.
            if (userInfo.data.email) {
              prefillData.email = userInfo.data.email;
            }
            // If the API provides a contact, use it.
            if (userInfo.data.contact) {
              prefillData.contact = userInfo.data.contact;
            }
          }
          console.log("Prefill data from API:", prefillData);
        } else {
          console.warn(
            "Could not fetch user info for prefill, using data from JWT."
          );
        }
      } catch (error) {
        console.error("Error fetching user info for prefill:", error);
      }
    }

    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    // 2. Create a Razorpay Order on your backend
    // This is a crucial step for security and proper payment flow.
    // Your backend should call Razorpay's Orders API.
    // For demonstration, this step is commented out. You MUST implement it.
    
    const orderResponse = await fetch('https://stage-api-zenit.trinodepointers.com/api/razorpay/order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiToken}` },
      body: JSON.stringify({ plan_id: plan.id, amount: plan.amount * 100, currency: 'INR' })
    });
    const orderData = await orderResponse.json();
    // Log the full response from the order creation API for debugging.
    console.log("Full Order Response from API:", orderData);

    // Safely access the order_id and handle potential errors.
    if (!orderData || !orderData.data || !orderData.data.razorpay_order) {
      console.error("Error: Could not retrieve a valid order_id from the API.", orderData);
      return; // Stop the payment process
    }

    const order_id = orderData.data.razorpay_order.id;
    console.log("Successfully retrieved Razorpay Order ID:", order_id);
    // 3. Configure Razorpay checkout options
    const options = {
      // IMPORTANT: Replace with your Razorpay Key ID.
      // It's best to store this in an environment variable.
      key: "rzp_live_JnJ6rggY5DfDUS",
      amount: plan.amount * 100, // amount in the smallest currency unit (paise)
      currency: "INR",
      name: "Zenit Plans",
      description: `Purchase ${plan.name}`,
      image: "https://via.placeholder.com/150", // A link to your logo
      order_id: order_id, // Use the order_id from your backend
      handler: function (response) {
        alert(`Payment successful. Payment ID: ${response.razorpay_payment_id}`);
        // 4. Verify the payment on your backend
        // Post this response to your backend to verify the payment signature
        // and activate the user's subscription.
        console.log("Payment successful:", response);
        // Example verification call:
        // await fetch('https://your-backend.com/api/verify-payment', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify(response)
        // });
      },
      prefill: prefillData,
      theme: {
        color: '#236B7B',
      },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.on("payment.failed", function (response) {
      alert(`Payment failed: ${response.error.description}`);
      console.error("Payment Failed:", response.error);
    });
    paymentObject.open();
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <h2>Loading plans...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <main className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="alert alert-danger text-center p-4 shadow-sm">
              <FaExclamationTriangle size={40} className="mb-3 text-danger" />
              <h4 className="alert-heading">An Error Occurred</h4>
              <p className="mb-1">{error}</p>
              <hr />
              <p className="mb-0">Please try logging into the app again. If the problem persists, please contact support.</p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="container py-5">
        <div className="text-center mb-5">
          <h1 className="text-primary">Subscription Plans</h1>
          <p className="text-muted">Choose the plan that fits your needs</p>
        </div>
        <div className="row g-4">
          {plans.map((plan) => (
            <div key={plan.id} className="col-md-3">
              <div
                className={`card h-100 border-${plan.color} shadow-sm ${
                  plan.popular ? "bg-light" : ""
                }`}
              >
                <div className="card-header text-center bg-primary text-white">
                  <h4 className="my-0">{plan.name}</h4>
                </div>
                <div className="card-body text-center d-flex flex-column">
                  <h1 className="card-title pricing-card-title">
                    <small className="text-muted" style={{ fontSize: '1.0rem' }}>Rs. </small>
                    {plan.price}
                    <small className="text-muted"></small>
                  </h1>
                  <table className="table text-start mb-3 bg-transparent">
                    <tbody>
                      {plan.features.map((feature, i) => (
                        <tr key={i}>
                          <td className="py-2 ps-0 bg-transparent" style={{ width: '30px' }}>
                            {feature.icon && (
                              <feature.icon className="text-primary" />
                            )}
                          </td>
                          <td className="py-2 px-0 bg-transparent d-flex justify-content-between">
                            <span>{feature.text}</span>
                            {feature.value && <strong>{feature.value}</strong>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {plan.description && (
                    <p className="text-muted small mb-4">{plan.description}</p>
                  )}
                  <button
                    className={`btn btn-${plan.color} mt-auto align-self-center`}
                    onClick={() => handlePayment(plan)}
                  >
                    {plan.button}
                  </button>
                </div>
                {plan.popular && (
                  <div className="card-footer text-center">
                    <span className="badge bg-success">Most Popular</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
      <footer className="text-center text-muted py-4 border-top bg-light">
        <p className="mb-0">Copyright © 2024 Zenit</p>
      </footer>
    </>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/plans/:jwtToken" element={<PlansPage />} />
      <Route
        path="*"
        element={
          <div className="container py-5 text-center">
            <h2>Invalid URL</h2>
            <p>Please use a valid URL with an authentication token, like <code>/plans/your_jwt_token</code>.</p>
          </div>
        }
      />
    </Routes>
  );
}

export default App;
